import React from "react";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Edit,
  Building2,
  Globe,
  FileText,
} from "lucide-react";

interface Clinic {
  clinic_id: number;
  external_clinic_id: string;
  clinic_name: string;
  legal_name: string;
  registration_no: string;
  tax_id: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  email: string;
  phone_number: string;
  timezone: string;
  preferred_language: string;
  notes: string;
  is_active: boolean;
  created_at: string;
  white_label_settings: {
    logo: string;
    email: string;
    logo_url: string;
    subdomain: string;
    about_link: string;
    brand_name: string;
    app_password: string;
    contact_link: string;
    primary_color: string;
    terms_of_service_link: string;
    privacy_statement_link: string;
  };
}

interface DetailsTabProps {
  clinic: Clinic;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ clinic }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Account Information
          </h3>
          {/* <button className="inline-flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700">
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </button> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">
              Basic Information
            </h4>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Account Name:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.clinic_name}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Legal Name:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.legal_name}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Registration No:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.registration_no}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Tax ID:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.tax_id}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">External ID:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.external_clinic_id}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Status:</span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    clinic.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {clinic.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">
              Contact Information
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900">{clinic.email}</span>
              </div>
              <div className="flex items-center">
                <Phone className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900">
                  {clinic.phone_number}
                </span>
              </div>
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                <div className="text-sm text-gray-900">
                  <p>{clinic.street_address}</p>
                  <p>
                    {clinic.city}, {clinic.state} {clinic.postal_code}
                  </p>
                  <p>{clinic.country}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900">
                  Created {formatDate(clinic.created_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Information */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            System Settings
          </h3>
          {/* <button className="inline-flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700">
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </button> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">
              Preferences
            </h4>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Timezone:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.timezone}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Language:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.preferred_language.toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">Notes</h4>
            <div className="flex items-start">
              <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
              <p className="text-sm text-gray-900">
                {clinic.notes || "No notes available"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* White Label Settings */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            White Label Settings
          </h3>
          {/* <button className="inline-flex items-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700">
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </button> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">Branding</h4>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Brand Name:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.white_label_settings.brand_name}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Subdomain:</span>
                <p className="text-sm font-medium text-gray-900">
                  {clinic.white_label_settings.subdomain}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Primary Color:</span>
                <div className="flex items-center mt-1">
                  <div
                    className="w-4 h-4 rounded border mr-2"
                    style={{
                      backgroundColor:
                        clinic.white_label_settings.primary_color,
                    }}
                  ></div>
                  <p className="text-sm font-medium text-gray-900">
                    {clinic.white_label_settings.primary_color}
                  </p>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-600">Logo URL:</span>
                <div className="flex items-center mt-1">
                  {clinic.white_label_settings.logo_url && (
                    <img
                      src={clinic.white_label_settings.logo_url}
                      alt="Account Logo"
                      className="w-8 h-8 object-contain mr-2"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )}
                  <p className="text-sm text-gray-900 break-all">
                    {clinic.white_label_settings.logo_url || "No logo set"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-3">
              Contact & Links
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-900">
                  {clinic.white_label_settings.email}
                </span>
              </div>
              <div className="flex items-start">
                <Globe className="h-4 w-4 text-gray-400 mr-2" />
                <div className="text-sm text-gray-900">
                  <p
                    style={{
                      marginTop: "-2px",
                    }}
                  >
                    About: {clinic.white_label_settings.about_link}
                  </p>
                  <p className="mt-1">
                    Contact: {clinic.white_label_settings.contact_link}
                  </p>
                  <p className="mt-1">
                    Terms: {clinic.white_label_settings.terms_of_service_link}
                  </p>
                  <p className="mt-1">
                    Privacy:{" "}
                    {clinic.white_label_settings.privacy_statement_link}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsTab;
